import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Agent, agentActions } from "../../redux/reducers/agentSlice";
import { DataTable } from "../../components/DataTable";
import { getColorForName } from "../../utils/colors";
import { useAppDispatch, useAppSelector } from "../../redux";
import { Container, View, Text, Card, CardHeader, CardTitle, CardContent } from "../../components/base";
import { OrganizationApprovalNotifications } from "../../components/OrganizationApprovalNotifications";
import { OrganizationTestNotifications } from "../../components/OrganizationTestNotifications";
import { PageLayout } from "../../components/PageLayout";

export const AgentsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const agentEntities = useAppSelector((state) => state.agent.entities);
  const isLoading = useAppSelector((state) => state.agent.isLoading);

  // Memoize agents to prevent unnecessary re-renders
  const agents = useMemo(() => Object.values(agentEntities), [agentEntities]);

  // Calculate last modified agent
  const lastModified = useMemo(() => {
    if (agents.length === 0) return null;

    const sortedByUpdate = [...agents].sort((a, b) => {
      const aTime = a.lastActivityAt || a.updatedAt;
      const bTime = b.lastActivityAt || b.updatedAt;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });

    const mostRecent = sortedByUpdate[0];
    const now = new Date();
    const updated = new Date(mostRecent.lastActivityAt || mostRecent.updatedAt);
    const diffMs = now.getTime() - updated.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let timeAgo;
    if (diffMins < 60) {
      timeAgo = diffMins <= 1 ? "1 minute ago" : `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      timeAgo = diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
    } else if (diffDays < 7) {
      timeAgo = diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
    } else {
      const diffWeeks = Math.floor(diffDays / 7);
      timeAgo = diffWeeks === 1 ? "1 week ago" : `${diffWeeks} weeks ago`;
    }

    return {
      id: mostRecent.id,
      name: mostRecent.name || "Unnamed",
      timeAgo: timeAgo,
      isUnnamed: !mostRecent.name,
    };
  }, [agents]);

  const onClickItem = (id: string) => {
    navigate(`/agents/${id}/overview`);
  };

  const onClickCreate = () => {
    navigate("/agents/new");
  };

  const handleTestSelect = (agentId: string, testName: string) => {
    navigate(`/agents/${agentId}/testing?test=${encodeURIComponent(testName)}`);
  };

  const filterAgents = (searchQuery: string, item: Record<string, unknown>) => {
    const agent = item as Agent;
    const agentName = agent.name?.toLowerCase() || "";
    const agentDescription = agent.description?.toLowerCase() || "";

    return agentName.includes(searchQuery.toLowerCase()) || agentDescription.includes(searchQuery.toLowerCase());
  };

  const sortAgents = (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const agentA = a as Agent;
    const agentB = b as Agent;

    const aTime = agentA.lastActivityAt || agentA.updatedAt;
    const bTime = agentB.lastActivityAt || agentB.updatedAt;
    return new Date(bTime).getTime() - new Date(aTime).getTime();
  };

  useEffect(() => {
    dispatch(agentActions.get({}));
  }, [dispatch]);

  return (
    <PageLayout
      title="Agents"
      description=""
    >
      {/* Organization Test Notifications */}
      <OrganizationTestNotifications
        agentIds={agents.map((agent) => agent.id)}
        agentEntities={agentEntities}
        onTestSelect={handleTestSelect}
      />

      {/* Stats Cards */}
      <View className="flex flex-col sm:flex-row gap-4 pt-8 px-4 sm:px-6 lg:px-8">
        <Card className="p-4 w-full sm:w-[200px]">
          <Text className="text-xs uppercase tracking-wider text-gray-500 mb-1 pr-2">Total Agents</Text>
          <View>
            <Text className="text-lg font-bold">{agents.length}</Text>
          </View>
        </Card>
        <Card className="p-4 w-full sm:w-[280px]">
          <Text className="text-xs uppercase tracking-wider text-gray-500 mb-1 pr-2">Last Modified</Text>
          {lastModified ? (
            <View>
              <Text
                className={`text-lg font-bold pr-2 ${getColorForName(lastModified.name, true)} ${lastModified.isUnnamed ? "italic" : ""} hover:underline cursor-pointer`}
                onClick={() => navigate(`/agents/${lastModified.id}/overview`)}
              >
                {lastModified.name}
              </Text>
              <Text className="text-sm text-gray-500">{lastModified.timeAgo}</Text>
            </View>
          ) : (
            <Text className="text-lg font-bold text-gray-500">No agents yet</Text>
          )}
        </Card>
      </View>

      <DataTable
        key={agents.length}
        entityName="agent"
        emptyMessage={`Create your first AI agent to get started.\nAgents can have their own voice and personality.`}
        filter={filterAgents}
        sort={sortAgents}
        isLoading={isLoading}
        onClickCreate={onClickCreate}
        items={agents}
        onRowClick={onClickItem}
        showIcons={true}
        iconName="user"
        timestampLabel="Last Activity"
      />
    </PageLayout>
  );
};
